<template>
  <v-card flat class="ma-4 outlined-card">
    <v-card-title class="d-flex align-center">
      {{ $t("users.settings.menuItems.settings") }}
      <v-spacer></v-spacer>
      <v-tooltip location="top">
        <template v-slot:activator="{ props }">
          <span v-bind="props">
            <v-icon :disabled="editPreferences" @click="editPreferences = true"
              >mdi-pencil</v-icon
            >
          </span>
        </template>
        <span>{{ $t("common.actions.edit") }}</span>
      </v-tooltip>
    </v-card-title>
    <v-card-text>
      <v-form
        ref="editUserPreferences"
        autocomplete="off"
        v-model="valid"
        :disabled="!editPreferences"
      >
        <v-row>
          <v-col cols="12" lg="6" class="pb-0">
            <v-select
              v-model="language"
              :label="$t('common.language')"
              :items="languageItems"
              item-title="name"
              item-value="code"
              variant="underlined"
              prepend-icon="mdi-translate"
            ></v-select>
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="12" lg="6" class="pb-0">
            <v-select
              v-model="unitOption"
              :label="$t('users.settings.preferences.units')"
              :items="unitItems"
              item-title="name"
              item-value="value"
              variant="underlined"
              prepend-icon="mdi-ruler"
            ></v-select>
          </v-col>
        </v-row>
        <v-row v-if="canToggleWeeklyReports">
          <v-col cols="12" lg="6" class="pb-0">
            <v-row no-gutters>
              <v-col cols="auto" class="pr-3 pt-2">
                <v-icon :disabled="!editPreferences">mdi-email-newsletter</v-icon>
              </v-col>
              <v-col>
                <v-label style="font-size: 12px;">
                  {{ $t("users.settings.notifications.sendWeeklyReports") }}
                </v-label>
                <v-checkbox
                  v-model="sendWeeklyReports"
                  hide-details
                  class="mt-1 pt-0"
                  :label="
                    $t('users.settings.notifications.sendWeeklyReportsDesc')
                  "
                ></v-checkbox>
              </v-col>
            </v-row>
          </v-col>
        </v-row>
      </v-form>
    </v-card-text>

    <v-card-actions v-if="editPreferences" class="pr-4 pb-4">
      <v-spacer></v-spacer>
      <v-btn
        class="mr-2"
        :disabled="!valid || isUpdating"
        :loading="isUpdating"
        @click.stop="onSavePreferences"
      >
        {{ $t("common.actions.save") }}
      </v-btn>
      <v-btn :disabled="isUpdating" @click.stop="onCancelPreferences">
        {{ $t("common.actions.cancel") }}
      </v-btn>
    </v-card-actions>

    <v-snackbar
      v-model="snackbar"
      :timeout="2000"
      location="top"
      theme="dark"
      color="green-darken-1"
    >
      {{ snackbarText }}

      <template v-slot:actions>
        <v-btn variant="text" @click="snackbar = false">
          {{ $t("common.actions.close") }}
        </v-btn>
      </template>
    </v-snackbar>
  </v-card>
</template>

<script>
import languages from "@/utils/languages"
import { mapActions } from "vuex"
import fetchClient from "@/utils/fetchClient"

export default {
  name: "UserPreferences",
  data() {
    return {
      valid: false,
      snackbar: false,
      editPreferences: false,
      isUpdating: false,
      sendWeeklyReports: true,
      language: this.$store.getters.langCode,
      unitOption: "metric",
      initialView: "map"
    }
  },
  components: {},
  created() {},
  mounted() {
    this.fetchUserDetails()
  },
  computed: {
    snackbarText() {
      return this.$t("common.saveSuceeded")
    },
    loggedInUser() {
      return this.$store.getters.loggedInUser
    },
    canToggleWeeklyReports() {
      return (
        this.$store.getters.isAdmin ||
        this.$store.getters.isService ||
        this.$store.getters.isLocalAdmin
      )
    },
    unitItems() {
      return [
        {
          name: this.$t("common.enums.measurementSystem.metric"),
          value: "metric"
        },
        {
          name: this.$t("common.enums.measurementSystem.imperial"),
          value: "imperial"
        }
      ]
    },
    languageItems() {
      // languages.supported has one entry per country (used to map a country
      // to a language elsewhere), so multiple countries share the same
      // language and code here - de-duplicate by code for the select.
      const seen = new Set()
      return languages.supported.filter(lang => {
        if (seen.has(lang.code)) {
          return false
        }
        seen.add(lang.code)
        return true
      })
    }
  },
  watch: {},
  methods: {
    ...mapActions(["setError"]),

    async fetchUserDetails() {
      await this.$store.dispatch("fetchUserDetails", {
        tenantUid: this.loggedInUser.tenantUid,
        uid: this.loggedInUser.uid
      })
      const res = this.$store.getters.userDetails

      if (res != null) {
        if (res.preferences != null) {
          if (res.preferences?.language) {
            this.language = res.preferences.language
          }
          if (res.preferences?.units) {
            this.unitOption = res.preferences.units
          }
          if (res.preferences?.initialView) {
            this.initialView = res.preferences.initialView
          }
        }
        if (res.notifications !== null) {
          this.sendWeeklyReports = res.notifications?.weeklyReports
        }
      }
    },
    async onSavePreferences() {
      this.isUpdating = true
      try {
        if (this.canToggleWeeklyReports) {
          // Only save weekly report settings if user is allowed to change it.
          // Users having user role cannot get weekly reports, thus it's hidden.
          const url = `/api/v1/tenants/${this.loggedInUser.tenantUid}/users/${this.loggedInUser.uid}/notifications`
          await fetchClient().putOrThrow(url, {
            weeklyReports: this.sendWeeklyReports
          })
        }

        let preferences = {
          language: this.language,
          units: this.unitOption,
          initialView: this.initialView
        }
        await this.$store
          .dispatch("updateUserPreferences", preferences)
          .catch(() => {
            // Error already displayed by dispatched action.
          })
        this.$root.setLanguage(this.language) //Set language after "updateUserPreferences" is completed, to not update language in database 2nd time
        this.snackbar = true
        this.editPreferences = false
      } catch (error) {
        this.setError(error)
      } finally {
        this.isUpdating = false
      }
    },
    async onCancelPreferences() {
      this.editPreferences = false
      await this.fetchUserDetails()
    }
  }
}
</script>

<style scoped>
.outlined-card {
  border: thin solid rgba(0, 0, 0, 0.12);
}
</style>
